import { 
  DealMetadata, 
  SimilarDeal, 
  RedFlag, 
  SeverityLevel, 
  ModelIssue, 
  BidEstimate, 
  RiskFactor 
} from '../../types';

// Mock Underwriting Support Data
export const mockDealMetadata: DealMetadata[] = [
  {
    id: '1',
    name: 'Parkview Apartments',
    location: 'Phoenix, AZ',
    propertyType: 'Multifamily',
    equitySize: 25000000,
    debtTerms: '65% LTV, 4.2% fixed, 10-year term',
    uploadDate: '2023-04-05T09:15:00Z',
    documentId: '4',
  },
];

export const mockSimilarDeals: SimilarDeal[] = [
  {
    id: '1',
    name: 'Fresno Apartments',
    similarityScore: 87,
    location: 'Fresno, CA',
    propertyType: 'Multifamily',
    equitySize: 22000000,
    irr: 15.3,
    capRate: 5.1,
    explanation: 'Similar market demographics, property age, and unit mix. Slightly smaller equity check but comparable cap rate.',
  },
  {
    id: '2',
    name: 'Desert View Residences',
    similarityScore: 75,
    location: 'Tucson, AZ',
    propertyType: 'Multifamily',
    equitySize: 18000000,
    irr: 14.8,
    capRate: 5.3,
    explanation: 'Similar Southwest market with comparable demographics. Smaller property but similar vintage and renovation scope.',
  },
  {
    id: '3',
    name: 'Sunbelt Apartments Portfolio',
    similarityScore: 68,
    location: 'Multiple - AZ, NV, TX',
    propertyType: 'Multifamily',
    equitySize: 45000000,
    irr: 16.2,
    capRate: 4.9,
    explanation: 'Larger portfolio but includes properties in similar markets with comparable tenant profiles.',
  },
];

export const mockRedFlags: RedFlag[] = [
  {
    id: '1',
    issue: 'Below-market rent growth assumptions',
    severity: SeverityLevel.Critical,
    explanation: 'Sponsor model assumes 2% annual rent growth vs. market average of 3.5% over the last 5 years.',
    dismissed: false,
  },
  {
    id: '2',
    issue: 'Deferred maintenance not fully accounted for',
    severity: SeverityLevel.Moderate,
    explanation: 'CapEx budget appears to be $250K below what comparable renovations would require based on our cost database.',
    dismissed: false,
  },
  {
    id: '3',
    issue: 'Recent tenant lawsuit not disclosed in OM',
    severity: SeverityLevel.Critical,
    explanation: 'Public records show a class action lawsuit filed by tenants in 2022 regarding security deposit handling.',
    dismissed: false,
  },
  {
    id: '4',
    issue: 'Flood zone designation changed in 2022',
    severity: SeverityLevel.Low,
    explanation: 'Property was reclassified from Zone X to Zone AE, which may impact insurance costs.',
    dismissed: false,
  },
];

export const mockModelIssues: ModelIssue[] = [
  {
    id: '1',
    issue: 'Unusually low renovation cost per unit',
    explanation: '$3.2K vs. average $7.5K for similar scope renovations in our database',
    impact: 'Could understate CapEx by approximately $1.1M total',
  },
  {
    id: '2',
    issue: 'Aggressive lease-up timeline',
    explanation: 'Model assumes 15 units/month vs. market average of 8-10 units/month',
    impact: 'May extend stabilization by 4-6 months',
  },
  {
    id: '3',
    issue: 'Exit cap rate too low',
    explanation: '4.25% exit cap vs. 4.75% market average for similar assets',
    impact: 'Could overstate exit value by approximately $5.2M',
  },
];

export const mockBidEstimate: BidEstimate = {
  id: '1',
  minBid: 22100000,
  maxBid: 23500000,
  targetIRR: 15,
  holdPeriod: 5,
  leverageConstraint: '65% LTV maximum',
  justification: 'Based on comparable sales in Phoenix metro ($175K-$185K/unit), adjusted for property condition and location premium. Range accounts for renovation cost uncertainty and rent growth variability.',
};

export const mockRiskFactors: RiskFactor[] = [
  {
    id: '1',
    summary: 'CapEx Overrun',
    impact: '-120bps IRR if renovation costs exceed budget by 20%',
    mitigationStrategy: 'Include 15% contingency in budget and phase renovations to validate cost assumptions with initial units',
    liked: null,
  },
  {
    id: '2',
    summary: 'Slower Rent Growth',
    impact: '-150bps IRR if rent growth averages 2% instead of projected 3.5%',
    mitigationStrategy: 'Focus on value-add improvements with demonstrated ROI; implement strategic renovation sequencing',
    liked: null,
  },
  {
    id: '3',
    summary: 'Extended Lease-up Period',
    impact: '-80bps IRR if stabilization takes 6 months longer than projected',
    mitigationStrategy: 'Allocate additional marketing budget and implement tiered concession strategy to accelerate absorption',
    liked: null,
  },
  {
    id: '4',
    summary: 'Interest Rate Increase',
    impact: '-100bps IRR for each 50bps increase in financing rate',
    mitigationStrategy: 'Secure rate lock at acquisition; evaluate hedging options or prepaid rate lock extensions',
    liked: null,
  },
];
